import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Target, Building, AlertTriangle, CheckCircle, RefreshCw, Star, Trophy, Award, TrendingUp, GraduationCap, Sparkles, ArrowRight, Phone, Mail, Clock, MapPin, Heart, Shield, Globe } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { allCourses } from '../data/courses';
import FloatingLabelInput from '../components/FloatingLabelInput';
import api from '../utils/axios';

// Enhanced components
const StatCard = ({ icon: Icon, number, label, description, color, delay = 0 }) => (
    <motion.div 
        className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20 text-center group hover:bg-white/20 transition-all duration-300"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay }}
        whileHover={{ scale: 1.05 }}
    >
        <div className="flex items-center justify-center w-12 h-12 bg-white/20 rounded-xl mb-4 mx-auto group-hover:scale-110 transition-transform duration-300">
            <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="text-3xl font-bold text-white mb-2">{number}</div>
        <div className="text-white/90 font-medium mb-1">{label}</div>
        <div className="text-white/70 text-sm">{description}</div>
    </motion.div>
);

const FeatureCard = ({ icon: Icon, title, description, delay = 0 }) => (
    <motion.div 
        className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-gray-100 dark:border-gray-700 group hover:-translate-y-2"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay }}
    >
        <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
            <Icon className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300">
            {title}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{description}</p>
    </motion.div>
);

const TestimonialCard = ({ name, role, company, testimonial, rating, delay = 0 }) => (
    <motion.div 
        className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-gray-100 dark:border-gray-700 group hover:-translate-y-2"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay }}
    >
        <div className="flex items-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center mr-4 group-hover:scale-105 transition-transform duration-300">
                <span className="text-white font-bold text-lg">{name.charAt(0)}</span>
            </div>
            <div>
                <h4 className="font-bold text-gray-900 dark:text-white text-lg">{name}</h4>
                <p className="text-primary-600 dark:text-primary-400 font-medium">{role}</p>
                <p className="text-sm text-gray-500">{company}</p>
            </div>
        </div>
        <div className="flex items-center mb-4">
            {[...Array(rating)].map((_, i) => (
                <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
            ))}
        </div>
        <blockquote className="text-gray-700 dark:text-gray-300 italic leading-relaxed">
            "{testimonial}"
        </blockquote>
    </motion.div>
);

const Enquire = () => {
    const location = useLocation();
    const courseDetails = location.state || {};
    
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        course: courseDetails.courseTitle || '',
        message: ''
    });

    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState('idle'); // idle, loading, success, error
    const [errorMessage, setErrorMessage] = useState('');

    const courseOptions = Object.values(allCourses).flat().map(c => c.title);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const filledFields = Object.values(formData).filter(value => typeof value === 'string' && value.trim() !== '').length;
        const totalFields = 4; // name, email, phone, course are required
        setProgress(Math.round((filledFields / totalFields) * 100));
    }, [formData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        // Clear error message when user starts typing
        if (status === 'error') {
            setStatus('idle');
            setErrorMessage('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('loading');
        setErrorMessage(''); // Clear any previous error messages
        
        try {
            await api.post('/enquiries', formData);
            setStatus('success');
            // Reset form after success
            setTimeout(() => {
                setStatus('idle');
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    course: '',
                    message: ''
                });
            }, 3000);
        } catch (err) {
            // Handle different types of errors
            if (err.response) {
                // Server responded with error status
                const errorData = err.response.data;
                if (errorData.message) {
                    setErrorMessage(errorData.message);
                } else if (err.response.status === 400) {
                    setErrorMessage('Please check your form data and try again.');
                } else if (err.response.status === 500) {
                    setErrorMessage('Server error. Please try again later.');
                } else {
                    setErrorMessage('Something went wrong. Please try again.');
                }
            } else if (err.request) {
                // Network error - no response received
                setErrorMessage('Network error. Please check your internet connection and try again.');
            } else {
                // Other errors
                setErrorMessage('An unexpected error occurred. Please try again.');
            }
            
            setStatus('error');
        }
    };

    const fadeInUp = {
        initial: { opacity: 0, y: 30 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    const staggerContainer = {
        animate: {
            transition: {
                staggerChildren: 0.1
            }
        }
    };



    return (
        <div className="bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 pt-20 overflow-hidden">
            {/* Enhanced Hero Section */}
            {/* <section className="relative min-h-screen flex items-center">
                <div className="absolute inset-0 bg-gradient-professional opacity-95"></div>
                <div className="absolute inset-0 bg-dots-dark opacity-20"></div>
                
                <div className="container-max relative z-10 py-20">
                    <div className="text-center text-white">
                        <motion.div
                            className="inline-flex items-center bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-3 mb-8"
                            {...fadeInUp}
                        >
                            <GraduationCap className="w-5 h-5 mr-2 text-yellow-400" />
                            <span className="text-sm font-medium">Start Your Programming Journey</span>
                        </motion.div>

                        <motion.h1 
                            className="text-5xl md:text-7xl font-bold mb-8 leading-tight"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            Transform Your{' '}
                            <span className="bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
                                Career Today
                            </span>
                        </motion.h1>

                        <motion.p 
                            className="text-xl md:text-2xl mb-12 max-w-4xl mx-auto leading-relaxed text-gray-100"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            Join thousands of successful graduates who have built amazing careers with OSOP. 
                            Start your programming journey today with India's most trusted online education platform.
                        </motion.p>

                        <motion.div 
                            className="flex flex-col md:flex-row justify-center items-center gap-8 max-w-4xl mx-auto"
                            variants={staggerContainer}
                            initial="initial"
                            animate="animate"
                        >
                            <div className="w-full md:w-1/3">
                                <StatCard 
                                    icon={Award}
                                    number="26+"
                                    label="Years Of Training & Consulting"
                                    description="In tech education"
                                    delay={0}
                                />
                            </div>
                            <div className="w-full md:w-1/3">
                                <StatCard 
                                    icon={Users}
                                    number="15K+"
                                    label="Students Trained"
                                    description="Across various domains"
                                    delay={0.1}
                                />
                            </div>
                            <div className="w-full md:w-1/3">
                                <StatCard 
                                    icon={Target}
                                    number="100%"
                                    label="Satisfied Learners"
                                    description="Happy with our training"
                                    delay={0.2}
                                />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section> */}

            <div className="container-max py-20 space-y-32">
                {/* Why Choose OSOP */}
                {/* Section removed as per request */}

                {/* Enquiry Form Section */}
                <section id="enquiry-form">
                    <motion.div 
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="heading-secondary mb-6">Start Your Journey Today</h2>
                        <p className="text-lead max-w-3xl mx-auto">
                            Take the first step towards your dream career. Submit your enquiry and our admissions team will contact you within 24 hours.
                        </p>
                    </motion.div>

                    <div className="max-w-3xl mx-auto">
                        <motion.div 
                            className="bg-white/90 dark:bg-gray-800/90 p-10 sm:p-14 rounded-3xl shadow-2xl border border-gray-100 dark:border-gray-700 backdrop-blur-md"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            {status === 'success' ? (
                                <motion.div 
                                    className="text-center py-12"
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.5 }}
                                >
                                    <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                                        <CheckCircle className="w-10 h-10 text-white" />
                                    </div>
                                    <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                                        Enquiry Submitted Successfully!
                                    </h3>
                                    <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
                                        Thank you for your interest in OSOP. Our admissions team will contact you within 24 hours to discuss your career goals and course options.
                                    </p>
                                    <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                                        <div className="bg-primary-50 dark:bg-primary-900/20 p-6 rounded-2xl">
                                            <h4 className="font-bold text-primary-600 dark:text-primary-400 mb-2">What's Next?</h4>
                                            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                                                <li>• Admission counselor call within 24 hours</li>
                                                <li>• Personalized course recommendation</li>
                                                <li>• Free demo class invitation</li>
                                                <li>• Career guidance session</li>
                                            </ul>
                                        </div>
                                        <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-2xl">
                                            <h4 className="font-bold text-green-600 dark:text-green-400 mb-2">Quick Contact</h4>
                                            <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                                                <div className="flex items-center">
                                                    <Phone className="w-4 h-4 mr-2" />
                                                    <span>+91 76490 98000</span>
                                                </div>
                                                <div className="flex items-center">
                                                    <Mail className="w-4 h-4 mr-2" />
                                                    <span>info@osopcoding.com</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ) : (
                                <>
                                    {/* Course Details Section - Show if course details were passed */}
                                    {courseDetails.courseTitle && (
                                        <motion.div 
                                            className="bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 p-6 rounded-2xl mb-8 border border-primary-200 dark:border-primary-700"
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.5 }}
                                        >
                                            <div className="flex items-center mb-4">
                                                <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center mr-4">
                                                    <GraduationCap className="w-6 h-6 text-white" />
                                                </div>
                                                <div>
                                                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">Selected Course</h4>
                                                    <p className="text-primary-600 dark:text-primary-400 font-medium">{courseDetails.courseTitle}</p>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                                {courseDetails.coursePrice && (
                                                    <div className="text-center">
                                                        <span className="text-gray-600 dark:text-gray-400">Price:</span>
                                                        <p className="font-bold text-gray-900 dark:text-white">₹{courseDetails.coursePrice}</p>
                                                    </div>
                                                )}
                                                {courseDetails.courseCategory && (
                                                    <div className="text-center">
                                                        <span className="text-gray-600 dark:text-gray-400">Category:</span>
                                                        <p className="font-bold text-gray-900 dark:text-white capitalize">{courseDetails.courseCategory.replace('-', ' ')}</p>
                                                    </div>
                                                )}
                                            </div>
                                        </motion.div>
                                    )}
                                    
                                    <div className="text-center mb-8">
                                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Get Started Today</h3>
                                        <p className="text-gray-600 dark:text-gray-400">Fill out the form below to begin your programming journey</p>
                                    </div>
                                    <form onSubmit={handleSubmit} className="space-y-6">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                            <div>
                                                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Full Name</label>
                                                <input
                                                    type="text"
                                                    id="name"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    required
                                                    className="w-full px-5 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/80 dark:bg-gray-900/80 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base transition-all"
                                                    placeholder="Enter your full name"
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Email Address</label>
                                                <input
                                                    type="email"
                                                    id="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    required
                                                    className="w-full px-5 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/80 dark:bg-gray-900/80 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base transition-all"
                                                    placeholder="Enter your email address"
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Phone Number</label>
                                                <input
                                                    type="tel"
                                                    id="phone"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    required
                                                    className="w-full px-5 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/80 dark:bg-gray-900/80 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base transition-all"
                                                    placeholder="Enter your phone number"
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="course" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Select Course</label>
                                                <select
                                                    id="course"
                                                    name="course"
                                                    value={formData.course}
                                                    onChange={handleChange}
                                                    required
                                                    className="w-full px-5 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/80 dark:bg-gray-900/80 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base transition-all"
                                                >
                                                    <option value="">Choose a course</option>
                                                    {courseOptions.map((course, idx) => (
                                                        <option key={idx} value={course}>{course}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div>
                                            <label htmlFor="message" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Message (Optional)</label>
                                            <textarea
                                                id="message"
                                                name="message"
                                                value={formData.message}
                                                onChange={handleChange}
                                                rows={4}
                                                className="w-full px-5 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/80 dark:bg-gray-900/80 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base transition-all resize-none"
                                                placeholder="Tell us more about your goals or questions (optional)"
                                            />
                                        </div>
                                        {status === 'error' && (
                                            <div className="text-red-600 bg-red-50 dark:bg-red-900/20 rounded-xl px-4 py-3 text-sm font-medium">
                                                {errorMessage}
                                            </div>
                                        )}
                                        <button
                                            type="submit"
                                            className="w-full py-3 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white font-bold text-lg shadow-lg hover:from-primary-600 hover:to-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200"
                                            disabled={status === 'loading'}
                                        >
                                            {status === 'loading' ? (
                                                <span className="flex items-center justify-center"><RefreshCw className="w-5 h-5 mr-2 animate-spin" />Submitting...</span>
                                            ) : (
                                                'Submit Enquiry'
                                            )}
                                        </button>
                                    </form>
                                </>
                            )}
                        </motion.div>
                    </div>
                </section>

                {/* Contact Information */}
                {/* <section className="bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/10 dark:to-primary-800/10 py-20 rounded-3xl">
                    <motion.div 
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="heading-secondary mb-6">Get In Touch</h2>
                        <p className="text-lead max-w-3xl mx-auto">
                            Have questions? Our admissions team is here to help you choose the right course for your career goals.
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 gap-12">
                        <motion.div 
                            className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-soft border border-gray-100 dark:border-gray-700"
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                                <Building className="w-8 h-8 text-primary-600 mr-3" />
                                Corporate Head Office
                            </h3>
                            <div className="space-y-4">
                                <div className="flex items-start">
                                    <MapPin className="w-5 h-5 text-primary-500 mr-3 mt-1 flex-shrink-0" />
                                    <span className="text-gray-700 dark:text-gray-300">
                                        First Floor, Kohinoor Tower, Opp HDFC Bank, Rajmohalla Indore, Madhya Pradesh 452001
                                    </span>
                                </div>
                                <div className="flex items-center">
                                    <Phone className="w-5 h-5 text-primary-500 mr-3" />
                                    <a href="tel:+917649098000" className="text-gray-700 dark:text-gray-300 hover:text-primary-600 transition-colors">
                                        +91 76490 98000
                                    </a>
                                </div>
                                <div className="flex items-center">
                                    <Mail className="w-5 h-5 text-primary-500 mr-3" />
                                    <a href="mailto:info@osopcoding.com" className="text-gray-700 dark:text-gray-300 hover:text-primary-600 transition-colors">
info@osopcoding.com
                                    </a>
                                </div>
                                <div className="flex items-center">
                                    <Clock className="w-5 h-5 text-primary-500 mr-3" />
                                    <span className="text-gray-700 dark:text-gray-300">Mon - Sat: 9:00 AM - 8:00 PM</span>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div 
                            className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-soft border border-gray-100 dark:border-gray-700"
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                        >
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                                <GraduationCap className="w-8 h-8 text-primary-600 mr-3" />
                                Training Center
                            </h3>
                            <div className="space-y-4">
                                <div className="flex items-start">
                                    <MapPin className="w-5 h-5 text-primary-500 mr-3 mt-1 flex-shrink-0" />
                                    <span className="text-gray-700 dark:text-gray-300">
                                        2nd Floor, Sundaram Complex, Bhanwarkua Tower Square Road, Indore, Madhya Pradesh 452014
                                    </span>
                                </div>
                                <div className="flex items-center">
                                    <Phone className="w-5 h-5 text-primary-500 mr-3" />
                                    <a href="tel:+917649099000" className="text-gray-700 dark:text-gray-300 hover:text-primary-600 transition-colors">
                                        +91 76490 99000
                                    </a>
                                </div>
                                <div className="flex items-center">
                                    <Mail className="w-5 h-5 text-primary-500 mr-3" />
                                    <a href="mailto:training@osopcoding.com" className="text-gray-700 dark:text-gray-300 hover:text-primary-600 transition-colors">
training@osopcoding.com
                                    </a>
                                </div>
                                <div className="flex items-center">
                                    <Clock className="w-5 h-5 text-primary-500 mr-3" />
                                    <span className="text-gray-700 dark:text-gray-300">Mon - Sat: 10:00 AM - 7:00 PM</span>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </section> */}

                {/* Call to Action */}
                {/* <section className="text-center bg-gradient-professional p-16 rounded-3xl shadow-2xl relative overflow-hidden">
                    <div className="absolute inset-0 bg-dots-dark opacity-20"></div>
                    <div className="relative z-10">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                                Ready to Start Your Journey?
                            </h2>
                            <p className="text-xl text-gray-100 mb-10 max-w-3xl mx-auto leading-relaxed">
                                Don't wait - your future in tech starts today! Join thousands of successful students who have transformed their careers with OSOP.
                            </p>
                            <div className="flex flex-col sm:flex-row justify-center gap-6">
                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <a 
                                        href="#enquiry-form" 
                                        className="btn-hero bg-white text-primary-600 hover:bg-gray-100 px-10 py-4 rounded-2xl font-bold text-lg shadow-2xl hover:shadow-glow-lg inline-flex items-center justify-center"
                                    >
                                        <Sparkles className="w-6 h-6 mr-3" />
                                        Submit Enquiry
                                        <ArrowRight className="w-6 h-6 ml-3" />
                                    </a>
                                </motion.div>
                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <Link 
                                        to="/courses" 
                                        className="btn-hero bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary-600 px-10 py-4 rounded-2xl font-bold text-lg shadow-2xl hover:shadow-glow-lg inline-flex items-center justify-center"
                                    >
                                        <Trophy className="w-6 h-6 mr-3" />
                                        Browse Courses
                                        <ArrowRight className="w-6 h-6 ml-3" />
                                    </Link>
                                </motion.div>
                            </div>
                        </motion.div>
                    </div>
                </section> */}
            </div>
        </div>
    );
};

export default Enquire;
